import { StartOrConfirmAlignmentEventProperties } from "@/analytics/cad-bim-events-properties";
import { useCurrentProjectApiClient } from "@/components/common/project-provider/project-loading-context";
import { ModalSpinner } from "@/components/ui/modal-spinner";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { changeMode } from "@/store/mode-slice";
import {
  selectAlignedSheetForSheetToSheetAlignment,
  selectIncrementalSheetTransformForSheetToSheetAlignment,
  selectReferenceForSheetToSheetAlignment,
} from "@/store/modes/sheet-to-sheet-alignment-mode-selectors";
import { resetSheetToSheetAlignment } from "@/store/modes/sheet-to-sheet-alignment-mode-slice";
import { setActiveElement, setActiveSheets } from "@/store/selections-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import { selectAncestor } from "@faro-lotv/app-component-toolbox";
import { grey, useToast } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { isIElementAreaSection } from "@faro-lotv/ielement-types";
import {
  fetchProjectIElements,
  poseToMatrix4,
  selectIElement,
  selectIElementProjectApiLocalPose,
} from "@faro-lotv/project-source";
import { createMutationAlignSheetWithSheet } from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import { useCallback, useState, type JSX } from "react";
import { Quaternion, Vector3 } from "three";
import {
  alignmentTransformToMatrix4,
  IDENTITY,
} from "../alignment-modes-commons/alignment-transform";
import { SheetToSheetAlignmentProgressBar } from "./sheet-to-sheet-alignment-progress-bar";
import { SheetToSheetAlignmentSplitScreen } from "./sheet-to-sheet-alignment-split-screen";

/** @returns The overlay for sheet-to-sheet alignment mode */
export function SheetToSheetAlignmentModeOverlay(): JSX.Element {
  const { openToast } = useToast();
  const store = useAppStore();

  const [showSpinner, setShowSpinner] = useState(false);

  const dispatch = useAppDispatch();
  const client = useCurrentProjectApiClient();
  const { handleErrorWithToast } = useErrorHandlers();

  const alignedSheetId = useAppSelector(
    selectAlignedSheetForSheetToSheetAlignment,
  );

  const referenceSheetId = useAppSelector(
    selectReferenceForSheetToSheetAlignment,
  );

  const applySheetMutation = useCallback(async () => {
    Analytics.track<StartOrConfirmAlignmentEventProperties>(
      "confirmAlignment",
      {
        combination: "sheet-to-sheet",
      },
    );

    setShowSpinner(true);

    const alignedSheet = selectIElement(alignedSheetId)(store.getState());
    const referenceSheet = selectIElement(referenceSheetId)(store.getState());
    assert(
      alignedSheetId && alignedSheet && referenceSheetId && referenceSheet,
      "alignedSheet or referenceSheet not defined.",
    );

    const incrementalTransform =
      selectIncrementalSheetTransformForSheetToSheetAlignment(
        store.getState(),
      ) ?? IDENTITY;

    const incrementalMatrix = alignmentTransformToMatrix4(incrementalTransform);

    const alignedSheetWorldMatrix = selectIElementWorldMatrix4(alignedSheetId)(
      store.getState(),
    );
    const newWorldTransformMovingSheet = incrementalMatrix.multiply(
      alignedSheetWorldMatrix,
    );

    const sectionArea = selectAncestor(
      alignedSheet,
      isIElementAreaSection,
    )(store.getState());
    assert(sectionArea, "Section Area not found for selected aligned sheet.");

    const areaWorldMatrix = selectIElementWorldMatrix4(sectionArea.id)(
      store.getState(),
    );
    // get local transform matrix of the aligned sheet (relative to parent area)
    const sheetMatrix = areaWorldMatrix
      .invert()
      .multiply(newWorldTransformMovingSheet);

    // get local pose of the aligned sheet IElement
    const { pos, rot, scale } = selectIElementProjectApiLocalPose(
      alignedSheet,
      sheetMatrix,
    )(store.getState());

    // convert reference sheet IElement local pose to  matrix
    const referencePoseMatrix = poseToMatrix4(referenceSheet.pose);

    // convert moving sheet IElement local pose to matrix
    const sheetPoseMatrix = poseToMatrix4({
      pos,
      rot,
      scale,
      isWorldRot: false,
      gps: null,
    });

    // calculate moving sheet local matrix relative to reference sheet
    const relativeMatrix = referencePoseMatrix
      .invert()
      .multiply(sheetPoseMatrix);

    const relPos = new Vector3();
    const relRot = new Quaternion();
    const relScale = new Vector3();
    relativeMatrix.decompose(relPos, relRot, relScale);

    try {
      await client.applyMutations([
        createMutationAlignSheetWithSheet(alignedSheetId, referenceSheetId, {
          // set translation in y to zero to have sheet elevation equal to reference sheet
          pos: { x: relPos.x, y: 0, z: relPos.z },
          rot: { x: relRot.x, y: relRot.y, z: relRot.z, w: relRot.w },
          scale: relScale,
          isWorldRot: false,
          gps: null,
        }),
      ]);
    } catch (error) {
      handleErrorWithToast({
        title: "Failed to save new alignment",
        error,
      });
    }

    // Fetch the changed section area sub-tree and update the local copy of the project
    // that new alignment will be used without reloading whole project
    dispatch(
      fetchProjectIElements({
        fetcher: async () =>
          // Refresh the area node to get new transform
          await client.getAllIElements({
            ancestorIds: [sectionArea.id],
          }),
      }),
    );

    // at the end of alignment cycle reset temporary data to prevent reusing it in the next session of alignment
    dispatch(resetSheetToSheetAlignment());

    // force aligned sheet to be new visible to immediately show results of alignment
    dispatch(setActiveElement(alignedSheetId));
    dispatch(setActiveSheets([alignedSheetId]));

    setShowSpinner(false);
    openToast({
      title: "Alignment completed",
      variant: "success",
    });

    // after alignment force switch to 2D mode as most convenient to validate alignment result
    dispatch(changeMode("sheet"));
  }, [
    store,
    dispatch,
    alignedSheetId,
    openToast,
    client,
    referenceSheetId,
    handleErrorWithToast,
  ]);

  return (
    <>
      <ModalSpinner
        sx={{ color: grey[0], zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showSpinner}
      />
      <Stack
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <SheetToSheetAlignmentProgressBar apply={applySheetMutation} />
        <SheetToSheetAlignmentSplitScreen />
      </Stack>
    </>
  );
}
